import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';

interface DiskConfig {
  name: string;
  initialSize: number;
  maxSize: number;
  maxQuantity: number;
  pricePerGB: number;
  periods: DiskPeriod[];
}

interface DiskPeriod {
  months: number;
  isEnabled: boolean;
  discount?: {
    type: 'gift' | 'reduction' | 'discount';
    value: number;
  };
}

export default function ProductDisksPage() {
  const [config, setConfig] = useState<DiskConfig>({
    name: '数据盘',
    initialSize: 100,
    maxSize: 1000,
    maxQuantity: 10,
    pricePerGB: 0.0192,
    periods: Array.from({ length: 12 }, (_, i) => ({
      months: i + 1,
      isEnabled: false,
    })),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    if (!config.initialSize || config.initialSize <= 0) {
      newErrors.initialSize = '请输入有效的初始大小';
    }

    if (!config.maxSize || config.maxSize <= 0 || config.maxSize > 1000) {
      newErrors.maxSize = '数据盘最大支持1000GB';
    }

    if (config.initialSize > config.maxSize) {
      newErrors.initialSize = '初始大小不能大于最大支持大小';
    }

    if (!config.maxQuantity || config.maxQuantity <= 0) {
      newErrors.maxQuantity = '请输入有效的支持数量';
    }

    if (!config.pricePerGB || config.pricePerGB <= 0) {
      newErrors.pricePerGB = '请输入有效的价格';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    // 模拟保存
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-teal-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    toast.textContent = '保存成功';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const handlePeriodToggle = (months: number, isEnabled: boolean) => {
    setConfig({
      ...config,
      periods: config.periods.map((p) =>
        p.months === months ? { ...p, isEnabled } : p
      ),
    });
  };

  const handleDiscountChange = (
    months: number,
    type: 'gift' | 'reduction' | 'discount' | null,
    value: number
  ) => {
    setConfig({
      ...config,
      periods: config.periods.map((p) => {
        if (p.months === months) {
          if (type === null) {
            const { discount, ...rest } = p;
            return rest;
          }
          return { ...p, discount: { type, value } };
        }
        return p;
      }),
    });
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">数据盘商品</h1>
          <p className="text-sm text-gray-500 mt-1">
            配置数据盘商品的规格和价格信息
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">规则说明</h2>
            <div className="mt-3 space-y-2 text-sm text-gray-600">
              <p>• 目前，五年折旧的每月每 G 成本为 ¥0.0192</p>
              <p>• 单个数据盘最多支持1000G</p>
              <p>• 全部为 SSD 硬盘</p>
              <p>• 每用户最多支持购买10个数据盘</p>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                数据盘名称
              </label>
              <input
                type="text"
                value={config.name}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  初始大小 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={config.initialSize}
                    onChange={(e) => {
                      setConfig({ ...config, initialSize: Number(e.target.value) });
                      setErrors({ ...errors, initialSize: '' });
                    }}
                    placeholder="请输入初始大小"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.initialSize ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                    GB
                  </span>
                </div>
                {errors.initialSize && (
                  <p className="mt-1 text-sm text-red-500">{errors.initialSize}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  数据盘最大支持 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={config.maxSize}
                    onChange={(e) => {
                      setConfig({ ...config, maxSize: Number(e.target.value) });
                      setErrors({ ...errors, maxSize: '' });
                    }}
                    placeholder="请输入最大支持大小"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.maxSize ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                    GB
                  </span>
                </div>
                {errors.maxSize && (
                  <p className="mt-1 text-sm text-red-500">{errors.maxSize}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  数据盘支持数量 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={config.maxQuantity}
                  onChange={(e) => {
                    setConfig({ ...config, maxQuantity: Number(e.target.value) });
                    setErrors({ ...errors, maxQuantity: '' });
                  }}
                  placeholder="请输入支持数量"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    errors.maxQuantity ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.maxQuantity && (
                  <p className="mt-1 text-sm text-red-500">{errors.maxQuantity}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  每 G 价格 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.0001"
                    value={config.pricePerGB}
                    onChange={(e) => {
                      setConfig({ ...config, pricePerGB: Number(e.target.value) });
                      setErrors({ ...errors, pricePerGB: '' });
                    }}
                    placeholder="请输入每G价格"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.pricePerGB ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                    元
                  </span>
                </div>
                {errors.pricePerGB && (
                  <p className="mt-1 text-sm text-red-500">{errors.pricePerGB}</p>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">可选周期列表</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                        时长
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                        开关
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                        优惠类型
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">
                        优惠值
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {config.periods.map((period) => (
                      <tr key={period.months}>
                        <td className="px-3 py-2 text-gray-900">{period.months}个月</td>
                        <td className="px-3 py-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={period.isEnabled}
                              onChange={(e) =>
                                handlePeriodToggle(period.months, e.target.checked)
                              }
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-500"></div>
                          </label>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={period.discount?.type || ''}
                            onChange={(e) => {
                              const type = e.target.value as
                                | 'gift'
                                | 'reduction'
                                | 'discount'
                                | '';
                              if (type) {
                                handleDiscountChange(period.months, type, 0);
                              } else {
                                handleDiscountChange(period.months, null, 0);
                              }
                            }}
                            disabled={!period.isEnabled}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value="">无优惠</option>
                            <option value="gift">赠送</option>
                            <option value="reduction">减免</option>
                            <option value="discount">折扣</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          {period.discount && (
                            <div className="flex gap-2 items-center">
                              <input
                                type="number"
                                step={period.discount.type === 'discount' ? '0.1' : '1'}
                                value={period.discount.value}
                                onChange={(e) =>
                                  handleDiscountChange(
                                    period.months,
                                    period.discount!.type,
                                    Number(e.target.value)
                                  )
                                }
                                disabled={!period.isEnabled}
                                className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                              {period.discount.type === 'gift' && (
                                <span className="text-gray-600">天</span>
                              )}
                              {period.discount.type === 'reduction' && (
                                <span className="text-gray-600">元</span>
                              )}
                              {period.discount.type === 'discount' && (
                                <span className="text-gray-600">折</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 pb-6 border-t border-gray-200 pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
